/**
 * B6CrossFeedbackCriteriaEditor.jsx - Sprint 2
 * 
 * Editor de criterios de cross-feedback (B6).
 * Evaluación peer-to-peer entre equipos.
 * Los pesos deben sumar exactamente 100%.
 * Incluye escala de evaluación (típicamente 1-5 estrellas).
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  GripVertical,
  AlertCircle,
  CheckCircle2,
  X,
  Save,
  Star
} from 'lucide-react';

const B6CrossFeedbackCriteriaEditor = ({ criteria = [], onChange }) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    weight: 10,
    scale: { min: 1, max: 5 }
  });
  const [errors, setErrors] = useState({});
  const [totalWeight, setTotalWeight] = useState(0);

  // Calcular peso total
  useEffect(() => {
    const total = criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
    setTotalWeight(total);
  }, [criteria]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'El nombre del criterio es obligatorio';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'La descripción es obligatoria';
    }
    
    if (formData.weight < 1 || formData.weight > 100) {
      newErrors.weight = 'El peso debe estar entre 1 y 100';
    }
    
    // Validar que el peso total no exceda 100
    const otherWeightsSum = criteria
      .filter((_, i) => i !== editingIndex)
      .reduce((sum, c) => sum + (c.weight || 0), 0);
    
    if (otherWeightsSum + formData.weight > 100) {
      newErrors.weight = `El peso total excedería 100% (actualmente ${otherWeightsSum}%)`;
    }
    
    if (formData.scale.min >= formData.scale.max) {
      newErrors.scale = 'El mínimo debe ser menor que el máximo';
    }
    
    return newErrors;
  };

  const handleAdd = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const newCriteria = [...criteria, { ...formData, criterionId: Date.now().toString() }];
    onChange(newCriteria);
    resetForm();
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setFormData({ ...criteria[index] });
    setErrors({});
  };

  const handleUpdate = () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    const newCriteria = [...criteria];
    newCriteria[editingIndex] = formData;
    onChange(newCriteria);
    resetForm();
  };

  const handleDelete = (index) => {
    if (confirm('¿Eliminar este criterio de cross-feedback?')) {
      const newCriteria = criteria.filter((_, i) => i !== index);
      onChange(newCriteria);
    }
  };

  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newCriteria = [...criteria];
    [newCriteria[index - 1], newCriteria[index]] = [newCriteria[index], newCriteria[index - 1]];
    onChange(newCriteria);
  };

  const handleMoveDown = (index) => {
    if (index === criteria.length - 1) return;
    const newCriteria = [...criteria];
    [newCriteria[index], newCriteria[index + 1]] = [newCriteria[index + 1], newCriteria[index]];
    onChange(newCriteria);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      weight: 10,
      scale: { min: 1, max: 5 }
    });
    setIsAdding(false);
    setEditingIndex(null);
    setErrors({});
  };

  const getWeightColor = () => {
    if (totalWeight === 100) return 'text-green-600 bg-green-50 border-green-200';
    if (totalWeight > 100) return 'text-red-600 bg-red-50 border-red-200';
    return 'text-pink-600 bg-pink-50 border-pink-200';
  };

  const distributeWeightsEqually = () => {
    if (criteria.length === 0) return;
    
    const equalWeight = Math.floor(100 / criteria.length);
    const remainder = 100 - (equalWeight * criteria.length);
    
    const newCriteria = criteria.map((c, index) => ({
      ...c,
      weight: index === 0 ? equalWeight + remainder : equalWeight
    }));
    
    onChange(newCriteria);
  };

  const scalePresets = [
    { min: 1, max: 3, label: '1-3 (Bajo/Medio/Alto)' },
    { min: 1, max: 5, label: '1-5 (Estrellas estándar)' },
    { min: 1, max: 10, label: '1-10 (Detallado)' },
    { min: 0, max: 100, label: '0-100 (Porcentaje)' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Criterios Cross-Feedback (B6)
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Define cómo los equipos se evaluarán entre sí (peer-to-peer)
          </p>
        </div>
        {!isAdding && editingIndex === null && (
          <button
            onClick={() => setIsAdding(true)}
            className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agregar Criterio
          </button>
        )}
      </div>

      {/* Indicador de peso total */}
      <div className={`border rounded-lg p-4 ${getWeightColor()}`}>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              {totalWeight === 100 ? (
                <CheckCircle2 className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              <span className="font-semibold">
                Peso Total: {totalWeight}%
              </span>
            </div>
            <div className="w-full bg-white rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  totalWeight === 100 ? 'bg-green-600' :
                  totalWeight > 100 ? 'bg-red-600' : 'bg-pink-600'
                }`}
                style={{ width: `${Math.min(totalWeight, 100)}%` }}
              />
            </div>
          </div>
          {criteria.length > 0 && totalWeight !== 100 && (
            <button
              onClick={distributeWeightsEqually}
              className="ml-4 px-3 py-1 bg-white rounded text-sm font-medium hover:bg-opacity-80 transition-colors"
            >
              Distribuir equitativamente
            </button>
          )}
        </div>
        <p className="text-sm mt-2">
          {totalWeight === 100 ? '✅ Perfecto! Los pesos suman 100%' :
           totalWeight > 100 ? `⚠️ Excede en ${totalWeight - 100}% - ajusta los pesos` :
           `⚠️ Faltan ${100 - totalWeight}% - agrega más criterios o ajusta pesos`}
        </p>
      </div>

      {/* Lista de criterios */}
      {criteria.length > 0 && (
        <div className="space-y-3">
          <h4 className="font-medium text-gray-900">
            Criterios Definidos ({criteria.length})
          </h4>
          {criteria.map((criterion, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-4 flex items-start gap-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <GripVertical className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  disabled={index === criteria.length - 1}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <GripVertical className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900">
                        {criterion.name}
                      </h4>
                      <span className="text-sm px-2 py-0.5 rounded font-semibold bg-pink-100 text-pink-800">
                        {criterion.weight}%
                      </span>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Star className="w-3 h-3" />
                        {criterion.scale?.min || 1} - {criterion.scale?.max || 5}
                      </div>
                    </div>
                    <p className="text-sm text-gray-600">
                      {criterion.description}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <button
                      onClick={() => handleEdit(index)}
                      className="text-gray-400 hover:text-pink-600 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(index)}
                      className="text-gray-400 hover:text-red-600 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Preview de escala */}
                <div className="bg-pink-50 rounded p-2 mt-2 flex items-center gap-1">
                  <span className="text-xs text-pink-700 font-medium">Escala:</span>
                  {Array.from({ length: criterion.scale?.max || 5 }, (_, i) => (
                    <Star key={i} className="w-4 h-4 text-pink-400" />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Formulario de agregar/editar */}
      {(isAdding || editingIndex !== null) && (
        <div className="bg-pink-50 border border-pink-200 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-gray-900">
              {editingIndex !== null ? 'Editar Criterio' : 'Nuevo Criterio'}
            </h4>
            <button
              onClick={resetForm}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Nombre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nombre del Criterio *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ej: Creatividad de la solución"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
              {errors.name && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.name}
                </p>
              )}
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe qué evaluarán los equipos entre sí..."
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent resize-none"
              />
              {errors.description && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.description}
                </p>
              )}
            </div>

            {/* Peso */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Peso (%) *
              </label>
              <input
                type="number"
                value={formData.weight}
                onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) || 0 })}
                min="1"
                max="100"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
              />
              {errors.weight && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.weight}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Peso disponible: {100 - (totalWeight - (editingIndex !== null ? criteria[editingIndex].weight : 0))}%
              </p>
            </div>

            {/* Escala de evaluación */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Escala de Evaluación
              </label>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {scalePresets.map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => setFormData({ ...formData, scale: { min: preset.min, max: preset.max } })}
                    className={`p-3 border-2 rounded-lg text-sm transition-all ${
                      formData.scale.min === preset.min && formData.scale.max === preset.max
                        ? 'border-pink-500 bg-white'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Mínimo</label>
                  <input
                    type="number"
                    value={formData.scale.min}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      scale: { ...formData.scale, min: parseInt(e.target.value) || 0 }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Máximo</label>
                  <input
                    type="number"
                    value={formData.scale.max}
                    onChange={(e) => setFormData({ 
                      ...formData, 
                      scale: { ...formData.scale, max: parseInt(e.target.value) || 0 }
                    })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  />
                </div>
              </div>
              {errors.scale && (
                <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.scale}
                </p>
              )}
              
              {/* Preview */}
              <div className="mt-3 p-3 bg-white rounded border border-pink-200">
                <p className="text-xs text-gray-600 mb-2">Vista previa:</p>
                <div className="flex gap-1">
                  {Array.from({ length: formData.scale.max - formData.scale.min + 1 }, (_, i) => (
                    <Star key={i} className="w-5 h-5 text-pink-400" />
                  ))}
                </div>
              </div>
            </div>

            {/* Botones */}
            <div className="flex gap-3 pt-4 border-t border-pink-200">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-700 hover:bg-white rounded-lg transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={editingIndex !== null ? handleUpdate : handleAdd}
                className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Save className="w-4 h-4" />
                {editingIndex !== null ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Empty state */}
      {criteria.length === 0 && !isAdding && editingIndex === null && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Star className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Sin criterios de cross-feedback
          </h3>
          <p className="text-gray-600 mb-4">
            Define cómo los equipos evaluarán el trabajo de otros equipos
          </p>
          <button
            onClick={() => setIsAdding(true)}
            className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-2 rounded-lg inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Agregar Primer Criterio
          </button>
        </div>
      )}

      {/* Info note */}
      {criteria.length > 0 && !isAdding && editingIndex === null && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            💡 <strong>Tip:</strong> El cross-feedback permite que los equipos se evalúen entre sí.
            Esto fomenta la reflexión crítica y el aprendizaje colaborativo.
          </p>
        </div>
      )}
    </div>
  );
};

export default B6CrossFeedbackCriteriaEditor;
